<script setup lang="ts">
	import { useQueryClient } from '@tanstack/vue-query'
	import { reactive, ref, watch } from 'vue'
	import { useToast } from 'vue-toastification'

	import BreadCrumb, { Page } from '@/components/BreadCrumb.vue'
	import HasPermission from '@/components/HasPermission.vue'
	import LoadingModal from '@/components/LoadingModal.vue'
	import ModalComponent from '@/components/ModalComponent.vue'
	import ShadowBox from '@/components/ShadowBox.vue'
	import StyledButton from '@/components/StyledButton.vue'
	import StyledCombobox from '@/components/StyledCombobox.vue'
	import StyledInput from '@/components/StyledInput.vue'
	import StyledSelect from '@/components/StyledSelect.vue'
	import StyledTable from '@/components/StyledTable.vue'
	import StyledTextarea from '@/components/StyledTextarea.vue'

	import useHasPermissionUser from '@/composables/useHasPermissionUser'

	import { Hotel } from '@/interfaces/hotel'

	import PaginateNav from '@/modules/trips/components/PaginateNav.vue'

	import CoincidencesList from '../../../components/CoincidencesList.vue'
	import useHotelForm from '../../../composables/useHotelForm'
	import useHotels from '../../../composables/useHotels'
	import useSearchHotels from '../../../composables/useSearchHotels'
	import useSearchBrands from '../brands/composables/useSearchBrands'
	import useSearchHotelGroups from '../group/composables/useSearchHotelGroups'
	import HotelCard from './components/HotelCard.vue'
	import HotelFilters from './components/HotelFilters.vue'
	import ListHotels from './components/ListHotels.vue'
	import useHotelComments from './composables/useHotelComments'

	const {
		data,
		page,
		setPage,
		refetch,
		isLoading,
		isRefetching,
		filters,
		resetFilters,
		setQuery,
	} = useSearchHotels({
		immediate: true,
		queryKey: 'list-hotels',
	})

	const {
		data: brands,
		isLoading: loadingBrands,
		setQuery: setQueryBrand,
	} = useSearchBrands({
		immediate: true,
		queryKey: 'list-brands',
		errorHandling: false,
	})

	const {
		data: hotelGroups,
		isLoading: loadingHotelGroups,
		setQuery: setQueryHotelGroups,
	} = useSearchHotelGroups({
		immediate: true,
		queryKey: 'hotel-groups',
		errorHandling: false,
	})

	const handlePage = (newPage: number | string) => {
		const pageNumber = Number(newPage)

		if (pageNumber >= 1 && pageNumber <= data.value?.meta.lastPage!) {
			setPage(newPage)
			refetch()
		}
	}

	const pages = ref<Page[]>([
		{
			name: 'Proveedores de hoteleria',
			to: {
				name: 'hotel',
			},
		},
		{
			name: 'Hoteles',
			to: {
				name: 'hotel.hotels',
			},
		},
	])

	const {
		actionModal,
		clearState,
		form,
		handleCreate,
		handleEvaluation,
		hotels,
		isCoincidence,
		modalAction,
		createHotelLoading,
	} = useHotelForm()

	const hoverRating = ref(0)

	const isDropdownAction = ref(false)

	const handleDropdownAction = () => {
		isDropdownAction.value = !isDropdownAction.value
	}

	const isOpenFilters = ref(false)

	const isActionFilters = () => {
		isOpenFilters.value = !isOpenFilters.value
	}

	const filtersActive = () => {
		const activeFilters = Object.values(filters).filter(
			(value) =>
				value !== undefined && value !== '' && value !== null && value !== 0
		)

		return activeFilters.length > 0 ? `(${activeFilters.length})` : ''
	}

	const queryClient = useQueryClient()
	const toast = useToast()

	const {
		data: hotelComments,
		isLoading: isLoadingHotelsComments,
		setHotelId,
		hotelId,
		refetch: refetchHotelComments,
		createCommentMutationAsync,
		createCommentMutationLoading,
		isRefetching: isRefetchingHotelComments,
	} = useHotelComments({
		immediate: false,
		params: {
			id: 0,
		},
	})

	const actionModalComments = ref(false)

	const handleActionModalComments = (hotelId: string | number) => {
		setHotelId(Number(hotelId))
		actionModalComments.value = !actionModalComments.value
	}

	const { changeStateHotelAsyncMutation } = useHotels({
		immediate: false,
	})

	const formDeactivateHotel = reactive({
		comment: '',
	})

	const handleCreateComment = async () => {
		if (!formDeactivateHotel.comment) {
			toast.error('El comentario es requerido')
			return
		}

		await createCommentMutationAsync({
			id: hotelId.value,
			comment: formDeactivateHotel.comment,
		})

		formDeactivateHotel.comment = ''
		refetchHotelComments()
	}

	watch(hotelId, () => {
		refetchHotelComments()
	})

	const headers = {
		createdAt: 'Fecha',
		creatorName: 'Usuario',
		commentary: 'Comentario',
	}

	const activeModal = ref(false)

	const formChangeState = reactive({
		comment: '',
		hotelId: 0,
		stateId: 0,
		stateName: '',
	})

	const clearFormChangeState = () => {
		formChangeState.comment = ''
		formChangeState.hotelId = 0
		formChangeState.stateId = 0
		formChangeState.stateName = ''
	}

	const changeState = async () => {
		if (!formChangeState.comment) {
			toast.error('El comentario es requerido')
			return
		}

		await changeStateHotelAsyncMutation({
			id: formChangeState.hotelId,
			stateId: formChangeState.stateId,
			comment: formChangeState.comment,
		})

		await queryClient.invalidateQueries(['hotels'])

		activeModal.value = false
		clearFormChangeState()
	}

	const handleActiveModalChangeState = (hotelId: string, stateId: number) => {
		formChangeState.hotelId = Number(hotelId)
		formChangeState.stateId = stateId

		activeModal.value = !activeModal.value
	}
</script>

<template>
	<BreadCrumb :pages="pages" />

	<main
		class="flex flex-col items-center justify-center gap-4 p-5 w-fill lg:w-10/12 mx-auto"
	>
		<!-- Header with search and create button -->
		<div class="flex flex-col justify-between items-center w-full">
			<div class="flex items-center w-full justify-between">
				<div class="">
					<h1 class="text-3xl font-bold text-gray-900">Listado de Hoteles</h1>
				</div>

				<HasPermission
					:name-permission="'create-internal'"
					:entity-type="'App\\Models\\Hotel'"
				>
					<StyledButton
						class="bg-blue-600 hover:bg-blue-700 text-white py-3.5 px-6 rounded-xl transition-all duration-200 flex items-center gap-2 min-w-[160px] justify-center hover:scale-105"
						@click="
							() => {
								modalAction = true
							}
						"
					>
						<span class="material-symbols-outlined">add</span>
						Crear hotel
					</StyledButton>
				</HasPermission>
			</div>

			<!-- Filtres -->
			<div class="flex items-center gap-2 w-full mt-4">
				<StyledButton
					class="bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md border-gray-500 border transition-all duration-200 flex items-center gap-2 min-w-[160px] justify-center hover:scale-105"
					@click="isActionFilters"
				>
					<span class="material-symbols-outlined"> filter_alt </span>
					Filtros

					{{ filtersActive() }}
				</StyledButton>

				<HotelFilters
					:when="isOpenFilters"
					@close-slide-over="isOpenFilters = false"
					@save-slide-over="isActionFilters"
					:filters="filters"
					@reset-filters="() => resetFilters()"
					@setQueryHotel="setQuery"
				/>
			</div>
		</div>

		<div class="w-full block sm:block md:hidden">
			<template v-if="isLoading || isRefetching">
				<div class="flex justify-center items-center w-full h-[300px] b">
					<span class="material-symbols-outlined text-2xl animate-spin">
						autorenew
					</span>
					<p class="text-gray-500">Cargando hoteles....</p>
				</div>
			</template>
			<template v-else-if="data?.data.length === 0">
				<div class="flex justify-center items-center w-full h-[300px]">
					<p class="text-gray-500">No se encontraron resultados.</p>
				</div>
			</template>

			<template v-else>
				<HotelCard
					v-for="hotel in data?.data || []"
					:key="hotel.id"
					:hotel="hotel"
					@setHotelIdComments="handleActionModalComments"
					@setHotelIdChangeState="handleActiveModalChangeState"
				/>
			</template>
		</div>

		<!-- Table for tablet devices and desktops devices -->
		<div class="w-full hidden sm:block">
			<!-- List -->
			<ListHotels
				:hotels="data?.data || []"
				:isDropdownAction="isDropdownAction"
				@toggle-dropdown="handleDropdownAction"
				@action-modal="actionModal"
				:is-loading-hotels="isLoading"
				:is-refetching-hotels="isRefetching"
				@setHotelIdComments="handleActionModalComments"
				@setHotelIdChangeState="handleActiveModalChangeState"
			>
				<template #pagination>
					<div
						class="flex justify-end w-full"
						v-if="data?.data"
					>
						<PaginateNav
							:page="page"
							:pagination="data"
							:data="data?.data || []"
							:handle-page="handlePage"
						/>
					</div>
				</template>
			</ListHotels>
		</div>
	</main>

	<!-- Modal create  -->
	<ModalComponent
		:model-value="modalAction"
		@update:model-value="
			() => {
				clearState()
				actionModal()
			}
		"
	>
		<template #modalBody>
			<div class="p-5">
				<form
					@submit.prevent="handleEvaluation"
					class=""
				>
					<StyledInput
						v-model="form.name"
						label="Nombre del Hotel"
						placeholder="Nombre del hotel"
						class="flex-1 mb-3"
						:is-required="true"
						:focus="true"
						:disabled="isCoincidence"
					/>

					<HasPermission
						:name-permission="'viewAny-internal'"
						:entity-type="'App\\Models\\Brand'"
					>
						<StyledCombobox
							v-model="form.brand"
							placeholder="No aplica"
							class="flex-1 mb-3"
							:options="brands?.data || []"
							:loading-options="loadingBrands"
							:on-input-change="setQueryBrand"
							:show-input="!form.brand"
							label="Nombre de la marca"
						>
							<template #default="{ option }">
								<span>{{ option.name }}</span>
							</template>

							<template #input-overlap>
								<template v-if="form.brand">
									<span>{{ form.brand.name }}</span>

									<button
										class="material-symbols-outlined text-[22px]"
										@click="form.brand = undefined"
									>
										close
									</button>
								</template>
							</template>
						</StyledCombobox>
					</HasPermission>

					<HasPermission
						:name-permission="'viewAny-internal'"
						:entity-type="'App\\Models\\Group'"
					>
						<StyledCombobox
							v-model="form.group"
							placeholder="No aplica"
							class="flex-1 mb-3"
							:options="hotelGroups?.data || []"
							:loading-options="loadingHotelGroups"
							:on-input-change="setQueryHotelGroups"
							:show-input="!form.group"
							label="Nombre del grupo"
						>
							<template #default="{ option }">
								<span>{{ option.name }}</span>
							</template>

							<template #input-overlap>
								<template v-if="form.group">
									<span>{{ form.group.name }}</span>

									<button
										class="material-symbols-outlined text-[22px]"
										@click="form.group = undefined"
									>
										close
									</button>
								</template>
							</template>
						</StyledCombobox>
					</HasPermission>

					<div class="flex gap-2 items-center">
						<p class="text-gray-400">Rating</p>
						<template
							v-for="star in 5"
							:key="star"
						>
							<button
								type="button"
								@mouseover="hoverRating = star"
								@mouseleave="hoverRating = 0"
								@click="form.rating = star"
								class="material-symbols-outlined"
							>
								<span
									:class="[
										hoverRating >= star || form.rating >= star ?
											'text-yellow-500'
										:	'',
										'text-xl transition-all',
									]"
									>star</span
								>
							</button>
						</template>
					</div>

					<StyledButton
						v-if="!isCoincidence"
						type="submit"
						class="bg-blue-500 hover:bg-blue-700 text-white py-2 px-4 rounded ml-auto"
						:loading="createHotelLoading"
					>
						Crear hotel
					</StyledButton>

					<CoincidencesList
						v-if="isCoincidence"
						:data="hotels?.data || []"
						:isCoincidence="isCoincidence"
						:loadingCreating="createHotelLoading"
						:handle-action="handleCreate"
						:clear-state="clearState"
						:action-modal="actionModal"
					/>
				</form>
			</div>
		</template>
	</ModalComponent>

	<!-- Comments -->
	<ModalComponent
		:model-value="actionModalComments"
		@update:model-value="
			() => {
				actionModalComments = !actionModalComments
			}
		"
	>
		<template #modalBody>
			<div v-if="isLoadingHotelsComments || !isRefetchingHotelComments">
				<StyledTable
					title="Comentarios generales"
					create-toggle-title="Agregar comentario"
					on-delete-title="¿Está seguro de eliminar este comentario?"
					:headers="headers"
					:entries="hotelComments?.data || []"
					:on-create="handleCreateComment"
					:show-save-button="false"
					:permission-to-create="'create-comments-internal'"
					:entity-type="'App\\Models\\Hotel'"
				>
					<template #form>
						<StyledTextarea
							class="!w-full"
							v-model="formDeactivateHotel.comment"
							label="Comentario"
							placeholder="¿Qué ha sucedido?"
						/>

						<StyledButton
							class="bg-[#253C70] text-white h-fit ml-auto !p-[0.65rem] !py-[0.25rem]"
							:loading="createCommentMutationLoading"
						>
							<span class="material-symbols-outlined text-2xl"> save </span>
							<p>Guardar</p>
						</StyledButton>
					</template>
				</StyledTable>
			</div>

			<div v-else>
				<ul class="flex flex-col gap-2 p-5 justify-center items-center">
					<li class="flex items-center gap-2">
						<span class="material-symbols-outlined text-2xl animate-spin">
							autorenew
						</span>
						<p class="text-gray-500">Cargando comentarios....</p>
					</li>
				</ul>
			</div>
		</template>
	</ModalComponent>

	<!-- Change State hotel -->
	<ModalComponent
		:model-value="activeModal"
		@update:model-value="
			() => {
				activeModal = !activeModal
				clearFormChangeState()
			}
		"
	>
		<template #modalBody>
			<form @submit.prevent="changeState">
				<div class="p-5">
					<div class="border-b-2 border-gray-500 mb-3">
						<h1 class="text-xl font-bold">Cambiar estado del hotel</h1>
					</div>

					<StyledTextarea
						label="Comentario"
						placeholder="Comentario sobre el cambio de estado "
						v-model:model-value="formChangeState.comment"
						is-required
					/>

					<StyledButton
						type="submit"
						class="mt-5 ml-auto bg-blue-500 text-white"
					>
						Cambiar estado
					</StyledButton>
				</div>
			</form>
		</template>
	</ModalComponent>

	<!-- TODO add code amdeus modal -->

	<LoadingModal :when="isLoading" />
</template>
